import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useAccounts } from "../../api/hooks/useAccounts";
import { useCreateAccount } from "../../api/hooks/useCreateAccount";
import {
  dismissOnboardingWizard,
  isOnboardingWizardDismissed,
} from "../../lib/onboarding";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

// First-run onboarding wizard (L0-U10 session 1, ADR 0024 D1 / ADR 0031).
//
// Fixes the account-before-import trap structurally: a fresh tenant has zero
// accounts and would otherwise land on Import with a dead picker (account
// creation lives under Setup, which is Advanced-only since L0-U4). The wizard
// guides welcome -> create first account (inline) -> import or dashboard.
//
// It is skippable; the Import-page zero-accounts safety net is the hard floor
// if the user skips. The product tour and demo-mode "explore sample data"
// branch land in L0-U10 session 2 (deferred here).
type WizardStep = "welcome" | "create-account" | "next-step";

const MAX_ACCOUNT_NAME = 255;

export function FirstRunWizard() {
  const navigate = useNavigate();
  const accountsQuery = useAccounts();
  const createAccount = useCreateAccount();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<WizardStep>("welcome");
  const [accountName, setAccountName] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Once the wizard opens we keep it open through the remaining steps even
  // after the account is created (which makes the accounts list non-empty).
  // This latch also prevents the open effect from re-triggering after a skip.
  const [started, setStarted] = useState(false);

  const accounts = (accountsQuery.data?.items ?? []).filter((a) => !a.is_deleted);
  const hasNoAccounts = accountsQuery.isSuccess && accounts.length === 0;

  useEffect(() => {
    if (started) return;
    if (hasNoAccounts && !isOnboardingWizardDismissed()) {
      setOpen(true);
      setStarted(true);
    }
  }, [hasNoAccounts, started]);

  function close() {
    dismissOnboardingWizard();
    setOpen(false);
  }

  async function handleCreate() {
    const trimmed = accountName.trim();
    if (trimmed.length === 0) {
      setError("Enter a name for your account.");
      return;
    }
    setError(null);
    try {
      await createAccount.mutateAsync({ name: trimmed });
      setStep("next-step");
    } catch {
      setError("Could not create your account. Try again.");
    }
  }

  function goImport() {
    close();
    void navigate({ to: "/imports/new" });
  }

  function goDashboard() {
    close();
    void navigate({ to: "/dashboard" });
  }

  function goDemo() {
    close();
    void navigate({ to: "/demo" });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Escape / overlay-click / the close affordance all skip the wizard.
        if (!next) close();
      }}
    >
      <DialogContent data-testid="first-run-wizard">
        {step === "welcome" && (
          <>
            <DialogHeader>
              <DialogTitle>Welcome to your trading journal</DialogTitle>
              <DialogDescription>
                Let&rsquo;s get you set up. First create an account to hold your
                trades, then import your first broker statement.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Skip for now
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setStep("create-account");
                }}
              >
                Get started
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "create-account" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleCreate();
            }}
            className="space-y-4"
            noValidate
          >
            <DialogHeader>
              <DialogTitle>Create your first account</DialogTitle>
              <DialogDescription>
                An account groups the trades from one broker or strategy. You can
                add more later under Setup.
              </DialogDescription>
            </DialogHeader>
            <label className="flex flex-col gap-1 text-sm text-on-surface">
              Account name
              <Input
                autoFocus
                autoComplete="off"
                maxLength={MAX_ACCOUNT_NAME}
                placeholder="Main brokerage"
                aria-label="Account name"
                value={accountName}
                onChange={(e) => {
                  setAccountName(e.target.value);
                }}
              />
            </label>
            {error !== null && (
              <p role="alert" className="text-sm font-medium text-error">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={createAccount.isPending}
                onClick={() => {
                  setError(null);
                  setStep("welcome");
                }}
              >
                Back
              </Button>
              <Button type="submit" disabled={createAccount.isPending}>
                {createAccount.isPending ? "Creating…" : "Create account"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === "next-step" && (
          <>
            <DialogHeader>
              <DialogTitle>You&rsquo;re all set</DialogTitle>
              <DialogDescription>
                Account created. Import your first broker statement now, explore
                a sample journal, or head to your dashboard.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="sm:flex-col sm:items-stretch sm:gap-2">
              <Button type="button" onClick={goImport}>
                Import a statement
              </Button>
              <Button type="button" variant="outline" onClick={goDemo}>
                Explore demo data
              </Button>
              <Button type="button" variant="ghost" onClick={goDashboard}>
                Go to dashboard
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
